import AuthorizedDistributorBadge from "@/components/authorized-distributor-badge/authorizedDistributorBadge";
import LinkInNewTab from "@/app/ec/product/[basePath]/manufacturer-promotions/linkInNewTab";
import Modal from "@/components/design-system/modal";
import { FunctionComponent, ImgHTMLAttributes, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import styles from "./previewManufacturerPromoModal.module.scss";

/** Renders the Authorized Distributor image embedded in promo copy as a Badge; other images render as-is. */
function PromoMarkdownImage({ authorizedBadgeLabel, ...props }: PromoMarkdownImageProps) {
  const src = typeof props.src === "string" ? props.src : "";
  if (src.includes("authorized-dealer-badges")) {
    return <AuthorizedDistributorBadge label={authorizedBadgeLabel} />;
  }
  return <img alt={props.alt ?? ""} src={src} className={styles.promoImage} />;
}

export const PreviewManufacturerPromoModal: FunctionComponent<PreviewPromoProps> = ({
  showModal,
  manufacturerLogoLink,
  onCloseModal,
  promoInfo: { title, manName, description, information },
  authorizedBadgeLabel,
}) => {
  const markdownComponents = useMemo(
    () => ({
      a: LinkInNewTab,
      img: (props: ImgHTMLAttributes<HTMLImageElement>) => (
        <PromoMarkdownImage {...props} authorizedBadgeLabel={authorizedBadgeLabel} />
      ),
    }),
    [authorizedBadgeLabel],
  );

  return (
    <Modal size="skinny" sizeDownMd="medium" show={showModal} onHide={onCloseModal} title="Buy Get Promotions">
      <div className={styles.content}>
        {manufacturerLogoLink !== "" && (
          <div className={styles.mfrLogoContainer}>
            <img alt={manName} src={manufacturerLogoLink} className={styles.mfrLogo} />
          </div>
        )}
        <span className={styles.promoTitle}>{title}</span>
        {description && (
          <div className={styles.description}>
            <ReactMarkdown components={markdownComponents}>{description}</ReactMarkdown>
          </div>
        )}
        {information && (
          <div className={styles.otherInfo}>
            <ReactMarkdown components={markdownComponents}>{information}</ReactMarkdown>
          </div>
        )}
      </div>
    </Modal>
  );
};

type PreviewPromoProps = {
  showModal: boolean;
  promoInfo: ManufacturerPromotionDetail;
  onCloseModal: () => void;
  manufacturerLogoLink: string;
  /** Overrides the text of the authorized badge that replaces the dealer badge image in the promo copy. */
  authorizedBadgeLabel?: string;
};

interface PromoMarkdownImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  authorizedBadgeLabel?: string;
}
